import { Loader, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { text } from "../text/text";
import { legalPath, footerLegal } from "../text/legal";

const Contact = () => {
  const [language, setLanguage] = useState("ro");
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: "success" | "error", message: string }
  const formRef = useRef(null);

  let { lang } = useParams();
  if (!lang) lang = "ro";
  let content = text[language];
  const legal = footerLegal[language] ? footerLegal[language] : footerLegal.ro;

  useEffect(() => {
    if (!lang) setLanguage("ro");
    else setLanguage(lang);
  }, [lang]);

  // Cuvântul/etichetele din text: linkuri către paginile juridice în limba activă
  const dataProcessParts = (content.contact.dataProcess || "").split("@@");

  // Preia un token CSRF de la server (folosit o singură dată)
  const fetchCsrf = async () => {
    const r = await fetch("/reply/csrf.php", { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.token ? j.token : null;
  };

  const postForm = async (payload) => {
    const response = await fetch("/reply/contact-solicitare.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return response.json();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setFeedback(null);
    const form = document.getElementById("contact-form");
    const formData = new FormData(form);

    const data = Object.fromEntries(formData.entries());

    try {
      let token = await fetchCsrf();
      if (!token) {
        setFeedback({ type: "error", message: content.contact.error });
        return;
      }
      let result = await postForm({ ...data, csrf: token, lang: language });
      // token-ul expiră după folosire; reîncercăm o singură dată cu un token nou
      if (result.code === "csrf") {
        token = await fetchCsrf();
        if (!token) {
          setFeedback({ type: "error", message: content.contact.error });
          return;
        }
        result = await postForm({ ...data, csrf: token, lang: language });
      }
      if (result.status === "success") {
        setFeedback({ type: "success", message: result.message });
        form.reset();
      } else {
        setFeedback({
          type: "error",
          message: result.message || content.contact.error,
        });
      }
    } catch (err) {
      setFeedback({ type: "error", message: content.contact.error });
    } finally {
      setIsLoading(false);
    }
  };
  const handleButtonClick = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    }
  };

  return (
    <section id="contact" className="bg-white">
      <div className="w-4/5 mx-auto h-screen flex flex-col justify-around">
        <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-8xl 2xl:text-9xl text-[#003E47]">
          {content.contact.h1}
        </h1>
        <form
          onSubmit={handleSubmit}
          ref={formRef}
          id="contact-form"
          className="h-1/2 flex flex-col justify-evenly"
        >
          <label
            className="font-[satoshi,'Manrope'] text-sm font-bold text-[#26CAD3]"
            htmlFor="name"
          >
            {content.contact.field1} <span className="font-[arial]">*</span>
            <br />
          </label>
          <input
            id="name"
            className="w-full text-3xl bg-transparent border-b border-b-cyan-300 focus:outline-none text-emerald-950 font-[meta-pro]"
            name="name"
            type="text"
            maxLength={100}
            required
          />
          <br />
          <label
            className="font-[satoshi,'Manrope'] text-sm font-bold text-[#26CAD3]"
            htmlFor="mail"
          >
            {content.contact.field2} <span className="font-[arial]">*</span>
            <br />
          </label>
          <input
            id="mail"
            className="w-full text-3xl bg-transparent border-b border-b-cyan-300 focus:outline-none text-emerald-950 font-[meta-pro]"
            name="mail"
            type="email"
            maxLength={150}
            required
          />
          <br />
          <label
            className="font-[satoshi,'Manrope'] text-sm font-bold text-[#26CAD3]"
            htmlFor="phone"
          >
            {content.contact.field3} <span className="font-[arial]">*</span>
            <br />
          </label>
          <input
            id="phone"
            className="w-full text-3xl bg-transparent border-b border-b-cyan-300 focus:outline-none text-emerald-950 font-[meta-pro]"
            name="phone"
            type="text"
            maxLength={40}
            required
          />
          <br />
          <label
            className="font-[satoshi,'Manrope'] text-sm font-bold text-[#26CAD3]"
            htmlFor="message"
          >
            {content.contact.field4} <span className="font-[arial]">*</span>
            <br />
          </label>
          <textarea
            id="message"
            className="w-full text-3xl bg-transparent border-b border-b-cyan-300 focus:outline-none text-emerald-950 font-[meta-pro]"
            name="message"
            rows={1}
            maxLength={2000}
          />
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
          />
          <br />
        </form>
        <div className="flex flex-col justify-center items-center md:flex-row md:justify-around gap-y-4">
          <div className="flex items-start md:items-center max-w-xl">
            <ShieldCheck className="mr-3 mt-1 md:mt-0 shrink-0" color="#26CAD3" size={28} />{" "}
            <p className="font-[satoshi-thin,'Manrope'] text-xs md:text-sm text-muted-foreground w-fit leading-relaxed">
              {dataProcessParts[0]}
              <Link
                to={legalPath(language, "terms")}
                className="underline text-[#003E47] hover:text-[#26CAD3]"
              >
                {legal.terms}
              </Link>
              {dataProcessParts[2]}
              <Link
                to={legalPath(language, "privacy")}
                className="underline text-[#003E47] hover:text-[#26CAD3]"
              >
                {legal.privacy}
              </Link>
              {dataProcessParts[4]}
            </p>
          </div>
          <Button
            onClick={handleButtonClick}
            className={"bg-[#003E47] w-[100px] mt-4 md:mt-0"}
          >
            {isLoading ? (
              <p className="flex items-center">
                <Loader className=" w-4 h-4 animate-spin" />
              </p>
            ) : (
              content.contact.button
            )}
          </Button>
        </div>
        {feedback && (
          <p
            className={
              "text-center text-sm font-[satoshi,'Manrope'] " +
              (feedback.type === "success"
                ? "text-emerald-600"
                : "text-red-500")
            }
          >
            {feedback.message}
          </p>
        )}
      </div>
    </section>
  );
};

export default Contact;
